import { useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { CustomFontSizeDiv } from "./lead-agent-styles";

// Zeigt dem Leadservice-Agenten die Antworten aus dem KI-Interview des
// Hausbesitzers (Asana 3.4): kuratierte Slot-Werte kompakt, das komplette
// Frage-Antwort-Protokoll aufklappbar. Werte wie "unbekannt — …" sind
// bewusst so gespeichert (der Hausbesitzer weiß es nicht) und werden
// unverändert angezeigt — der Agent weiß dann, was er nicht erneut fragen
// muss. Reihenfolge = Frage-Dramaturgie des Interviews (funnelhub-backend,
// ai-interview.service.js SLOTS).
// Spiegel der Backend-Guards (funnelhub-backend/services/ai-interview.guards.js).
const UNKNOWN_VALUE = /^\s*unbekannt\b/i;
const PLACEHOLDER_VALUE =
  /^\s*(?:antwort|option|auswahl|frage|wert|feld|test|platzhalter|lorem)\s*(?:\d+|[a-d])?\s*$/i;
// Werte, die vor dem Guard vom 14.09. gespeichert wurden, können den internen
// Feldnamen noch als Präfix tragen („detail2: Ziegeldach") — der gehört nicht
// ins Panel. Das Backend säubert beim Schreiben und beim Lesen; hier derselbe
// Schnitt für Leads, die direkt aus der Lead-API kommen.
// Entfernt wird NUR der Name des eigenen Feldes: Ein Wert darf mit einem
// ANDEREN Feldnamen beginnen und meint ihn dann inhaltlich („Budget: 20.000 €,
// Dachsanierung" in der Beschreibung) — sonst verlöre der Satz seine Bedeutung.
const SLOT_LABELS = {
  beschreibung: "Projekt",
  umfang: "Umfang",
  zeitrahmen: "Zeitrahmen",
  kaufreife: "Entscheidungsstand",
  budget: "Budgetrahmen",
  erreichbarkeit: "Erreichbarkeit"
};
const slotKeyPrefix = (key) => {
  if (/^detail\d*$/i.test(key)) return /^\s*detail\s*\d*\s*:\s*/i;
  if (SLOT_LABELS[key])
    return new RegExp(`^\\s*(?:${key}|${SLOT_LABELS[key]})\\s*:\\s*`, "i");
  return null;
};
const cleanValue = (value, key) => {
  const text = String(value || "").trim();
  const pattern = slotKeyPrefix(key);
  return pattern ? text.replace(pattern, "").trim() : text;
};

const SLOT_ORDER = [
  "beschreibung",
  "umfang",
  "zeitrahmen",
  "kaufreife",
  "budget",
  "erreichbarkeit"
];

const InterviewSummary = ({ interview }) => {
  const { t } = useTranslation();
  const [showQa, setShowQa] = useState(false);
  const slots = interview?.slots || {};
  const rows = SLOT_ORDER.filter(
    (key) =>
      typeof slots[key] === "string" &&
      slots[key].trim() &&
      !PLACEHOLDER_VALUE.test(slots[key]) &&
      cleanValue(slots[key], key)
  );
  // Vertiefende Rückfragen (detail0..n) — oft der inhaltsreichste Teil.
  // Interne Felder (_pending), Nicht-Strings, Unbekannt-Marker und
  // Platzhalter („Antwort 4") bleiben draußen — gleiche Regel wie Backend-
  // Zusammenfassung und CC-Payload (Sarhads Test 11.09.). Kein Textfilter auf
  // „weiß nicht": das wird beim Schreiben abgefangen; als Lesefilter träfe er
  // echte Details („Wandfarbe weiß matt").
  const details = Object.entries(slots)
    .filter(
      ([key, value]) =>
        key.startsWith("detail") &&
        typeof value === "string" &&
        value.trim() &&
        !UNKNOWN_VALUE.test(value) &&
        !PLACEHOLDER_VALUE.test(value)
    )
    .map(([key, value]) => cleanValue(value, key))
    .filter(Boolean);
  const qa = (interview?.qa || []).filter(
    (pair) => pair?.question && pair?.answer
  );
  // Keine leere Sektion rendern (Interview übersprungen / Alt-Leads).
  if (!rows.length && !details.length && !qa.length) return null;

  return (
    <div className="my-3">
      <CustomFontSizeDiv $fs="12" className="text-secondary fw-bold">
        {t("leadAgent.interview.heading").toUpperCase()}
      </CustomFontSizeDiv>
      {rows.map((key) => (
        <div key={key} className="mb-1">
          <CustomFontSizeDiv $fs="12" className="text-secondary">
            {t(`leadAgent.interview.slots.${key}`)}
          </CustomFontSizeDiv>
          <CustomFontSizeDiv $fs="14" className="fw-bold">
            {cleanValue(slots[key], key)}
          </CustomFontSizeDiv>
        </div>
      ))}
      {details.length > 0 && (
        <div className="mb-1">
          <CustomFontSizeDiv $fs="12" className="text-secondary">
            {t("leadAgent.interview.details")}
          </CustomFontSizeDiv>
          <CustomFontSizeDiv $fs="14" className="fw-bold">
            {details.join(" · ")}
          </CustomFontSizeDiv>
        </div>
      )}
      {qa.length > 0 && (
        <>
          <Button
            variant="link"
            size="sm"
            className="p-0 text-decoration-none"
            onClick={() => setShowQa((prev) => !prev)}
          >
            {showQa
              ? t("leadAgent.interview.hideQa")
              : t("leadAgent.interview.showQa", { count: qa.length })}
          </Button>
          {showQa &&
            qa.map((pair, index) => (
              <div key={index} className="mb-2">
                <CustomFontSizeDiv $fs="12" className="text-secondary">
                  {pair.question}
                </CustomFontSizeDiv>
                <CustomFontSizeDiv $fs="14">{pair.answer}</CustomFontSizeDiv>
              </div>
            ))}
        </>
      )}
    </div>
  );
};

export default InterviewSummary;
